import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import {
  Description,
  GetApp,
  OpenInNew,
  PictureAsPdf,
  Image,
  VideoLibrary,
  Audiotrack,
  Archive,
  Code,
  TableChart,
  Slideshow,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { StatusBadge } from "../ui";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    preview: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 16,
      padding: 8,
      borderRadius: t.radius.sm,
      border: `1px solid ${t.line}`,
      backgroundColor: t.muted,
      "& img": {
        display: "block",
        maxWidth: "100%",
        maxHeight: 300,
        borderRadius: t.radius.badge,
      },
    },
    documentInfo: {
      display: "flex",
      alignItems: "flex-start",
      gap: 14,
      minWidth: 0,
    },
    iconTile: {
      flex: "none",
      width: 48,
      height: 48,
      borderRadius: t.radius.sm,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: t.accentTint.soft,
    },
    documentIcon: {
      fontSize: 26,
      color: t.accent,
    },
    documentDetails: {
      flex: 1,
      minWidth: 0,
    },
    documentName: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.4,
      color: t.text,
      overflowWrap: "anywhere",
    },
    documentType: {
      margin: "2px 0 8px",
      fontSize: 13,
      color: t.textSecondary,
    },
    actions: {
      flexWrap: "wrap",
    },
    closeButton: {
      marginRight: "auto",
    },
  };
});

const DocumentModal = ({ open, onClose, document }) => {
  const classes = useStyles();
  const [fileInfo, setFileInfo] = useState(null);

  useEffect(() => {
    if (document && document.mediaPath) {
      // Extrair informações do arquivo
      const fileName =
        document.mediaName || document.mediaPath.split("/").pop();
      const fileExtension = fileName.split(".").pop()?.toLowerCase();

      setFileInfo({
        name: fileName,
        extension: fileExtension,
        type: document.mediaType,
        url: document.mediaPath,
      });
    }
  }, [document]);

  const getDocumentIcon = (extension, mediaType) => {
    switch (extension) {
      case "pdf":
        return <PictureAsPdf className={classes.documentIcon} />;
      case "doc":
      case "docx":
      case "odt":
        return <Description className={classes.documentIcon} />;
      case "xls":
      case "xlsx":
      case "ods":
        return <TableChart className={classes.documentIcon} />;
      case "ppt":
      case "pptx":
      case "odp":
        return <Slideshow className={classes.documentIcon} />;
      case "zip":
      case "rar":
      case "7z":
      case "tar":
      case "gz":
      case "bz2":
        return <Archive className={classes.documentIcon} />;
      case "exe":
        return <GetApp className={classes.documentIcon} />;
      case "xml":
      case "json":
      case "html":
        return <Code className={classes.documentIcon} />;
      case "ret":
        return <Description className={classes.documentIcon} />;
      default:
        if (mediaType === "image")
          return <Image className={classes.documentIcon} />;
        if (mediaType === "video")
          return <VideoLibrary className={classes.documentIcon} />;
        if (mediaType === "audio")
          return <Audiotrack className={classes.documentIcon} />;
        return <Description className={classes.documentIcon} />;
    }
  };

  const getDocumentTypeName = (extension) => {
    const typeMap = {
      pdf: "Documento PDF",
      doc: "Documento Word",
      docx: "Documento Word",
      odt: "Documento OpenDocument",
      xls: "Planilha Excel",
      xlsx: "Planilha Excel",
      ods: "Planilha OpenDocument",
      ppt: "Apresentação PowerPoint",
      pptx: "Apresentação PowerPoint",
      odp: "Apresentação OpenDocument",
      zip: "Arquivo Compactado",
      rar: "Arquivo Compactado",
      "7z": "Arquivo Compactado",
      tar: "Arquivo Compactado",
      gz: "Arquivo Compactado",
      bz2: "Arquivo Compactado",
      xml: "Arquivo XML",
      json: "Arquivo JSON",
      html: "Arquivo HTML",
      ofx: "Arquivo OFX",
      msg: "E-mail Outlook",
      key: "Apresentação Keynote",
      numbers: "Planilha Numbers",
      pages: "Documento Pages",
      exe: "Executável",
      ret: "Arquivo RET",
    };

    return typeMap[extension] || "Documento";
  };

  const handleDownload = () => {
    if (fileInfo?.url) {
      window.open(fileInfo.url, "_blank");
    }
  };

  const handleOpen = () => {
    if (fileInfo?.url) {
      window.open(fileInfo.url, "_blank");
    }
  };

  if (!fileInfo) return null;

  const isImage = ["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(
    fileInfo.extension
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="paper">
      <DialogTitle>Visualizar Documento</DialogTitle>
      <DialogContent>
        {isImage && (
          <div className={classes.preview}>
            <img src={fileInfo.url} alt={fileInfo.name} />
          </div>
        )}
        <div className={classes.documentInfo}>
          {!isImage && (
            <div className={classes.iconTile}>
              {getDocumentIcon(fileInfo.extension, fileInfo.type)}
            </div>
          )}
          <div className={classes.documentDetails}>
            <div className={classes.documentName}>{fileInfo.name}</div>
            <div className={classes.documentType}>
              {getDocumentTypeName(fileInfo.extension)}
            </div>
            <StatusBadge
              color="neutral"
              size="sm"
              dot={false}
              label={fileInfo.extension.toUpperCase()}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} className={classes.closeButton}>
          Fechar
        </Button>
        <Button
          variant="outlined"
          startIcon={<OpenInNew />}
          onClick={handleOpen}
        >
          Abrir
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<GetApp />}
          onClick={handleDownload}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentModal;
